
























import Vue from "vue";
import { MicErrorKey } from "@joyz/app-core/models/microphone";
import { audioContext } from "@tt-app/utils/audio/audioContext";
import { MicConfig } from "@joyz/app-core/utils/microphone/common";
import { micPermissionInspector } from "@tt-app/utils/audio/micPermissionInspector";

import IconAlertCircle from "@joyz/app-core/assets/icons/transparent/AlertCircle.svg";

interface Data {
  error: string;
  icons: { [key: string]: string };
  micPermissionGranted: boolean | undefined;
}

interface Methods {
  checkMicPermissions: () => void;
  handleMicStartPress: () => void;
  handleMicStopPress: () => void;
  onConnectionError: (error: Error) => void;
  resetData: () => void;
  reinitMic: () => void;
  stopRecording: () => void;
}

interface Computed {
  derivedCustomError: string;
  derivedDisabled: boolean;
  micErrMsgs: { [key in MicErrorKey]?: string };
  micFinalConfig: MicConfig;
  effectiveMsgPressToStop: string;
  effectiveMsgTapToRecord: string;
}

interface Props {
  customError: string;
  disabled: boolean;
  infoAtBottom: boolean;
  loading: boolean;
  micConfig: MicConfig;
  micPermission: boolean | undefined;
  micTimeout: number;
  msgPressToStop: string | undefined;
  msgTapToRecord: string | undefined;
  showSilenceAlert: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "TtMic",

  props: {
    customError: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    infoAtBottom: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    micConfig: { type: Object as () => MicConfig, required: true },
    micPermission: { type: Boolean, default: undefined },
    micTimeout: { type: Number, default: 15000 },
    msgPressToStop: { type: String, default: undefined },
    msgTapToRecord: { type: String, default: undefined },
    showSilenceAlert: { type: Boolean, default: false }
  },

  data() {
    return {
      error: "",
      icons: {
        alertCircle: IconAlertCircle
      },
      micPermissionGranted: this.micPermission
    };
  },

  created() {
    if (this.micPermissionGranted === undefined) {
      this.checkMicPermissions();
    } else if (this.micPermissionGranted === false) {
      this.error = this.$__("mic.err.no-permission");
    }
  },

  computed: {
    derivedCustomError() {
      return this.error || this.customError;
    },

    derivedDisabled() {
      return this.disabled || !this.micPermissionGranted || !!this.derivedCustomError;
    },

    micFinalConfig() {
      return {
        impl: this.micConfig.impl,
        options: {
          audioCtx: audioContext,
          debug: this.$route.query.debug !== undefined,
          slackDebug: process.env.VUE_APP_MIC_SLACK_DEBUG === "true" || false,
          slackWebhook: process.env.VUE_APP_MIC_SLACK_WEBHOOK,
          queueSttBaseURL: process.env.VUE_APP_MIC_STT_QUEUE_URL,
          queueSttS3Bucket: process.env.VUE_APP_MIC_STT_QUEUE_S3,
          ...this.micConfig.options
        }
      };
    },
    micErrMsgs() {
      return {
        "mic-err_unknown": this.$__("mic.err.unknown"),
        // "mic-err_media-stream_abort": "MediaStream ABORT error",
        "mic-err_media-stream_not-allowed": this.$__("mic.err.no-permission"),
        // "mic-err_media-stream_not-found": "MediaStream NOT FOUND error",
        // "mic-err_media-stream_not-readable": "MediaStream NOT READABLE error",
        // "mic-err_media-stream_overconstrained": "MediaStream OVERCONSTRAINT error",
        // "mic-err_media-stream_security": "MediaStream SECURITY error",
        // "mic-err_media-stream_type": "MediaStream TYPE error",
        "mic-err_lifecycle_audio-context-not-available": "AudioContext not available",
        "mic-err_stt-proxy_end-of-stream": this.$__("mic.err.recognition-failed"),
        "mic-err_stt-proxy_soft-timeout": this.$__("mic.err.soft-timeout"),
        "mic-err_stt-proxy_failed-to-transcribe": this.$__("mic.err.recognition-failed"),
        "mic-err_stt-queue_no-data": this.$__("mic.err.recognition-failed"),
        "mic-err_stt-queue_api-call": this.$__("mic.err.recognition-failed"),
        "mic-err_stt-queue_no-queue-id-received": this.$__("mic.err.recognition-failed")
      };
    },

    effectiveMsgPressToStop() {
      return this.msgPressToStop || this.$__("mic.tap-to-stop");
    },
    effectiveMsgTapToRecord() {
      return this.msgTapToRecord || this.$__("mic.tap-to-record");
    }
  },

  beforeDestroy() {
    const jzmic = (this.$refs.microphone as any).mic;
    if (jzmic.status === "recording") {
      this.stopRecording();
    }
  },

  methods: {
    async checkMicPermissions() {
      const permissionGranted = await micPermissionInspector();

      if (!permissionGranted) {
        this.error = this.$__("mic.err.no-permission");
      } else {
        this.micPermissionGranted = true;
      }
    },

    handleMicStartPress() {
      this.$emit("mic-start-press");
    },

    handleMicStopPress() {
      this.$emit("mic-stop-press");
    },

    onConnectionError(error) {
      this.$emit("connection-error", error);
      this.reinitMic();
    },
    resetData() {
      const jzmic = this.$refs.microphone as any;
      if (jzmic) {
        (this.$refs.microphone as any).resetData();
      }
    },
    async reinitMic() {
      const jzmic = this.$refs.microphone as any;
      if (jzmic) {
        await (this.$refs.microphone as any).reinitMic();
      }
    },
    stopRecording() {
      const jzmic = this.$refs.microphone as any;
      if (jzmic) {
        jzmic.stopRecording();
      }
    }
  }
});
